import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useMouseParallax } from "../utils/mouseParallax";
import Cal, { getCalApi } from "@calcom/embed-react";
import SEO from "../components/SEO";
import Button from "../components/ui/Button";
import GridOverlay from "../components/ui/GridOverlay";
import emailjs from "emailjs-com";
import socialLinks from "../rs.json";

// Composant SVG animé pour l'enveloppe
const EnvelopeSVG = ({ isSubmitted }) => {
    const [animationComplete, setAnimationComplete] = useState(false);

    useEffect(() => {
        if (isSubmitted) {
            // Réinitialiser l'état d'animation après la durée de l'animation
            const timer = setTimeout(() => {
                setAnimationComplete(true);
            }, 3000); // Durée approximative de l'ensemble des animations

            return () => clearTimeout(timer);
        } else {
            setAnimationComplete(false);
        }
    }, [isSubmitted]);

    useEffect(() => {
        (async function () {
            const cal = await getCalApi({ namespace: "30min" });
            cal("ui", {
                theme: "light",
                hideEventTypeDetails: false,
                layout: "month_view",
            });
        })();
    }, []);

    return (
        <motion.div
            className="w-full max-w-xs mx-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{
                scale: 1,
                opacity: 1,
                y: isSubmitted && !animationComplete ? [-20, -60, -40] : 0,
                rotate:
                    isSubmitted && !animationComplete
                        ? [0, -5, 5, -3, 3, 0]
                        : 0,
            }}
            transition={{
                duration: isSubmitted ? 2 : 0.5,
                type: "spring",
                bounce: isSubmitted ? 0.4 : 0.2,
                y: {
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                    times: [0, 0.6, 1],
                    duration: 2.5,
                },
                rotate: {
                    times: [0, 0.2, 0.4, 0.6, 0.8, 1],
                    duration: 1.8,
                },
            }}
        >
            <motion.svg
                viewBox="0 0 120 80"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full"
                initial={{ scale: 1 }}
                animate={{
                    scale:
                        isSubmitted && !animationComplete
                            ? [1, 1.1, 1, 1.05, 1]
                            : 1,
                    rotateY:
                        isSubmitted && !animationComplete
                            ? [0, 15, 0, -15, 0]
                            : 0,
                    y:
                        isSubmitted && !animationComplete
                            ? [0, -5, 0, -3, 0]
                            : 0,
                }}
                transition={{
                    repeat: isSubmitted && !animationComplete ? 0 : 0,
                    duration: 3,
                    ease: "easeInOut",
                    y: {
                        repeat:
                            isSubmitted && !animationComplete ? Infinity : 0,
                        repeatType: "reverse",
                        duration: 2,
                        ease: "easeInOut",
                    },
                }}
            >
                {/* Enveloppe base */}
                <motion.rect
                    x="10"
                    y="20"
                    width="100"
                    height="60"
                    rx="5"
                    ry="5"
                    fill="url(#gradient)"
                    initial={{ fill: "url(#gradient)" }}
                    animate={{
                        fill:
                            isSubmitted && !animationComplete
                                ? [
                                      "url(#gradient)",
                                      "url(#gradientLight)",
                                      "url(#gradient)",
                                  ]
                                : "url(#gradient)",
                    }}
                    transition={{
                        repeat: 0,
                        duration: 2,
                    }}
                />

                {/* Définition des gradients */}
                <defs>
                    <linearGradient
                        id="gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#4f46e5" />
                        <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                    <linearGradient
                        id="gradientLight"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="100%" stopColor="#9333ea" />
                    </linearGradient>
                </defs>

                {/* Rabat avant de l'enveloppe */}
                <motion.path
                    d="M10,20 C10,20 10,20 15,20 L60,45 L105,20 C110,20 110,20 110,20 Z"
                    fill="url(#gradientDark)"
                    initial={{ rotateX: 0 }}
                    animate={{
                        rotateX: isSubmitted && !animationComplete ? -180 : 0,
                        translateY: isSubmitted && !animationComplete ? -5 : 0,
                    }}
                    transition={{ duration: isSubmitted ? 0.5 : 0 }}
                    style={{
                        transformOrigin: "60px 20px",
                        display:
                            isSubmitted && !animationComplete
                                ? "none"
                                : "block",
                    }}
                />

                {/* Gradient plus foncé pour les rabats */}
                <defs>
                    <linearGradient
                        id="gradientDark"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#4338ca" />
                        <stop offset="100%" stopColor="#6d28d9" />
                    </linearGradient>
                </defs>

                {/* Rabat arrière */}
                <motion.path
                    d="M10,80 C10,80 10,80 15,80 L60,55 L105,80 C110,80 110,80 110,80 Z"
                    fill="url(#gradientDark)"
                />

                {/* Contenu (lettre) */}
                <motion.rect
                    x="20"
                    y="30"
                    width="80"
                    height="40"
                    fill="#ffffff"
                    initial={{ y: 30 }}
                    animate={{
                        y: isSubmitted ? [-10, 30] : 30,
                        opacity: isSubmitted ? [1, 0] : 1,
                    }}
                    transition={{ duration: 0.5, delay: isSubmitted ? 0.2 : 0 }}
                />

                {/* Lignes de texte */}
                <motion.g
                    initial={{ opacity: 1 }}
                    animate={{ opacity: isSubmitted ? 0 : 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <rect
                        x="30"
                        y="37"
                        width="60"
                        height="2"
                        rx="1"
                        fill="#e0e0e0"
                    />
                    <rect
                        x="30"
                        y="44"
                        width="60"
                        height="2"
                        rx="1"
                        fill="#e0e0e0"
                    />
                    <rect
                        x="30"
                        y="51"
                        width="40"
                        height="2"
                        rx="1"
                        fill="#e0e0e0"
                    />
                    <rect
                        x="30"
                        y="58"
                        width="50"
                        height="2"
                        rx="1"
                        fill="#e0e0e0"
                    />
                </motion.g>

                {/* Détail d'adresse */}
                <motion.g
                    initial={{ opacity: 1 }}
                    animate={{ opacity: isSubmitted ? 0 : 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <rect
                        x="75"
                        y="65"
                        width="25"
                        height="10"
                        rx="1"
                        fill="url(#gradientDark)"
                    />
                    <rect
                        x="77"
                        y="68"
                        width="21"
                        height="1"
                        rx="0.5"
                        fill="#ffffff"
                    />
                    <rect
                        x="77"
                        y="71"
                        width="15"
                        height="1"
                        rx="0.5"
                        fill="#ffffff"
                    />
                </motion.g>

                {/* Effets visuels quand soumis */}
                {isSubmitted && !animationComplete && (
                    <>
                        <motion.g
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{
                                opacity: [0, 1, 0.8, 0],
                                scale: [0.5, 1.2, 1.5, 0],
                            }}
                            transition={{
                                times: [0, 0.3, 0.6, 1],
                                repeat: 1,
                                duration: 1.5,
                                delay: 0.2,
                            }}
                        >
                            <circle
                                cx="60"
                                cy="30"
                                r="20"
                                fill="url(#gradientLight)"
                                fillOpacity="0.6"
                            />
                        </motion.g>
                        <motion.g
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{
                                opacity: [0, 1, 0],
                                scale: [0.5, 1.5, 0],
                            }}
                            transition={{
                                times: [0, 0.5, 1],
                                repeat: 2,
                                duration: 1,
                                repeatDelay: 0.3,
                            }}
                        >
                            <circle cx="60" cy="30" r="3" fill="#ffffff" />
                            <circle cx="45" cy="25" r="2" fill="#ffffff" />
                            <circle cx="75" cy="25" r="2" fill="#ffffff" />
                            <circle cx="30" cy="30" r="1.5" fill="#ffffff" />
                            <circle cx="90" cy="30" r="1.5" fill="#ffffff" />
                        </motion.g>
                        <motion.path
                            d="M 60,20 L 65,15 M 60,20 L 55,15 M 60,20 L 60,10"
                            stroke="#ffffff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            initial={{ opacity: 0, pathLength: 0 }}
                            animate={{
                                opacity: [0, 1, 0],
                                pathLength: [0, 1, 1],
                                y: [0, -5, -10],
                            }}
                            transition={{
                                duration: 0.8,
                                delay: 0.5,
                                repeat: 2,
                                repeatDelay: 0.7,
                            }}
                        />
                    </>
                )}
            </motion.svg>
        </motion.div>
    );
};

/**
 * Accordion component for the sidebar
 */
const Accordion = ({ title, icon, children, isOpen, toggle }) => {
    return (
        <div className="mb-4 rounded-lg bg-white shadow-sm overflow-hidden">
            <button className="text-2xl md:text-2xl font-bold text-gray-900 p-4">
                {title}
            </button>

            <div className="p-4 pt-0">{children}</div>
        </div>
    );
};

/**
 * Formulaire de contact principal
 * Gère les 3 étapes du formulaire et l'envoi du message
 */
const Contact = () => {
    // États du formulaire
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
        services: [],
        otherService: "",
    });

    console.log(formData);

    // États de l'interface
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // État pour les erreurs de validation
    const [errors, setErrors] = useState({
        name: "",
        email: "",
    });

    // Références aux éléments DOM
    const sectionRef = useRef(null);
    const formRef = useRef(null);

    // Réinitialiser la position de défilement lors du chargement de la page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Effet de parallaxe à la souris
    useMouseParallax({ elementRef: sectionRef });

    // Liste des services
    const serviceOptions = [
        { id: "fullstack", name: "Développement Full Stack", icon: "🌐" },
        { id: "mobile", name: "Développement Mobile", icon: "📱" },
        { id: "ai", name: "Intégration IA", icon: "🤖" },
        { id: "design", name: "UI/UX Design", icon: "🎨" },
        { id: "seo", name: "SEO & Marketing", icon: "📈" },
        { id: "ecommerce", name: "E-commerce", icon: "🛒" },
        { id: "cms", name: "CMS & WordPress", icon: "📝" },
        { id: "maintenance", name: "Maintenance", icon: "🔧" },
        { id: "other", name: "Autres", icon: "✨" },
    ];

    // Observer pour animations au scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add(
                            "opacity-100",
                            "translate-y-0"
                        );
                        entry.target.classList.remove(
                            "opacity-0",
                            "translate-y-5"
                        );
                        // Unobserve after animation to improve performance
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: "0px 0px 50px 0px", // Pre-load a bit before elements come into view
            }
        );

        document
            .querySelectorAll(".animate-on-scroll")
            .forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Effacer l'erreur lorsque l'utilisateur commence à taper
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    // Fonction de validation de l'email
    const validateEmail = (email) => {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    // Fonction pour valider les champs de la première étape
    const validateFirstStep = () => {
        const newErrors = {};
        let isValid = true;

        // Validation du nom
        if (!formData.name.trim()) {
            newErrors.name = "Veuillez entrer votre nom";
            isValid = false;
        }

        // Validation de l'email
        if (!formData.email.trim()) {
            newErrors.email = "Veuillez entrer votre email";
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = "Veuillez entrer un email valide";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const toggleService = (serviceId) => {
        setFormData((prev) => ({
            ...prev,
            services: prev.services.includes(serviceId)
                ? prev.services.filter((id) => id !== serviceId)
                : [...prev.services, serviceId],
        }));
    };

    const nextStep = () => {
        // Vérifier les validations si on est à la première étape
        if (currentStep === 0) {
            const isValid = validateFirstStep();
            if (!isValid) return;
        }

        setCurrentStep((prev) => Math.min(prev + 1, 2));
    };

    const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Format the services array into a comma-separated string with proper descriptions
        const selectedServices = formData.services
            .map((serviceId) => {
                const service = serviceOptions.find((s) => s.id === serviceId);
                return service ? service.name : serviceId;
            })
            .join(", ");

        // Prepare template parameters
        const templateParams = {
            from_name: formData.name,
            from_email: formData.email,
            message: formData.message,
            services: selectedServices,
            other: formData.otherService,
        };

        try {
            // Send email using EmailJS
            await emailjs.send(
                "service_sw2oyv6", // Your EmailJS service ID
                "template_5dqtd8y", // Your EmailJS template ID
                templateParams,
                "zURIypL0HzxgaUBln" // Your EmailJS public key
            );

            // Success state
            setIsSubmitting(false);
            setIsSubmitted(true);

            // Optional: reset form data
            setFormData({
                name: "",
                email: "",
                message: "",
                services: [],
                otherService: "",
            });
        } catch (error) {
            console.error("Failed to send email:", error);
            setIsSubmitting(false);
            alert(
                "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer."
            );
        }
    };

    const resetForm = () => {
        setIsSubmitted(false);
        setCurrentStep(0);
        setFormData({
            name: "",
            email: "",
            message: "",
            services: [],
            otherService: "",
        });
    };

    // Fieldsets pour formulaire multi-étapes
    const fieldsets = [
        // Étape 1: Informations personnelles
        <motion.div
            key="step1"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
        >
            <h3 className="text-xl font-bold text-gray-800 mb-4">
                Vos informations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        Votre nom <span className="text-primary-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={() => {
                            if (!formData.name.trim()) {
                                setErrors((prev) => ({
                                    ...prev,
                                    name: "Veuillez entrer votre nom",
                                }));
                            }
                        }}
                        className={`w-full px-4 py-3 border ${
                            errors.name ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                        placeholder="John Doe"
                        required
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-500">
                            {errors.name}
                        </p>
                    )}
                </div>
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        Votre email <span className="text-primary-500">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={() => {
                            if (!formData.email.trim()) {
                                setErrors((prev) => ({
                                    ...prev,
                                    email: "Veuillez entrer votre email",
                                }));
                            } else if (!validateEmail(formData.email)) {
                                setErrors((prev) => ({
                                    ...prev,
                                    email: "Veuillez entrer un email valide",
                                }));
                            }
                        }}
                        className={`w-full px-4 py-3 border ${
                            errors.email ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                        placeholder="john@example.com"
                        required
                    />
                    {errors.email && (
                        <p className="mt-1 text-sm text-red-500">
                            {errors.email}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex justify-end mt-4">
                <Button
                    variant="primary"
                    onClick={nextStep}
                    className={`px-6 ${
                        !formData.name.trim() ||
                        !formData.email.trim() ||
                        !validateEmail(formData.email)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                    }`}
                    disabled={
                        !formData.name.trim() ||
                        !formData.email.trim() ||
                        !validateEmail(formData.email)
                    }
                >
                    Suivant <i className="ml-2 fas fa-arrow-right"></i>
                </Button>
            </div>
        </motion.div>,

        // Étape 2: Choix des services
        <motion.div
            key="step2"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
        >
            <h3 className="text-xl font-bold text-gray-800 mb-4">
                Choisissez vos services
            </h3>
            <p className="text-gray-600 text-sm mb-4">
                Sélectionnez un ou plusieurs services qui vous intéressent :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {serviceOptions.map((service) => (
                    <div
                        key={service.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            formData.services.includes(service.id)
                                ? "border-primary-500 bg-primary-50"
                                : "border-gray-200 hover:border-primary-300"
                        }`}
                        onClick={() => toggleService(service.id)}
                    >
                        <div className="flex items-center">
                            <span className="text-2xl mr-3">
                                {service.icon}
                            </span>
                            <span className="font-medium">{service.name}</span>
                            {formData.services.includes(service.id) && (
                                <span className="ml-auto bg-primary-500 text-white rounded-full p-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Champ pour "Autres" */}
            {formData.services.includes("other") && (
                <div className="mt-4">
                    <label
                        htmlFor="otherService"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        Précisez votre besoin :
                    </label>
                    <input
                        type="text"
                        id="otherService"
                        name="otherService"
                        value={formData.otherService}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Décrivez brièvement votre besoin..."
                    />
                </div>
            )}

            {/* Message d'avertissement si aucun service n'est sélectionné */}
            {formData.services.length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded-md border border-amber-200">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    Veuillez sélectionner au moins un service pour continuer
                </p>
            )}

            <div className="flex justify-between mt-4">
                <Button variant="outline" onClick={prevStep} className="px-6">
                    <i className="mr-2 fas fa-arrow-left"></i> Retour
                </Button>
                <Button
                    variant="primary"
                    onClick={nextStep}
                    className={`px-6 ${
                        formData.services.length === 0
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                    }`}
                    disabled={formData.services.length === 0}
                >
                    Suivant <i className="ml-2 fas fa-arrow-right"></i>
                </Button>
            </div>
        </motion.div>,

        // Étape 3: Message et soumission
        <motion.div
            key="step3"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
        >
            <h3 className="text-xl font-bold text-gray-800 mb-4">
                Votre message
            </h3>
            <div>
                <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                >
                    Décrivez votre projet{" "}
                    <span className="text-primary-500">*</span>
                </label>
                <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    placeholder="Parlez-nous de votre projet et de vos attentes..."
                    required
                ></textarea>
            </div>

            {/* Récapitulatif des services */}
            <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">
                    Services sélectionnés :
                </h4>
                <div className="flex flex-wrap gap-2">
                    {formData.services.map((serviceId) => {
                        const service = serviceOptions.find(
                            (s) => s.id === serviceId
                        );
                        return (
                            <span
                                key={serviceId}
                                className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm flex items-center"
                            >
                                {service?.icon} {service?.name}
                                {serviceId === "other" && formData.otherService
                                    ? ` (${formData.otherService})`
                                    : ""}
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-between mt-4">
                <Button variant="outline" onClick={prevStep} className="px-6">
                    <i className="mr-2 fas fa-arrow-left"></i> Retour
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6"
                >
                    {isSubmitting ? (
                        <>
                            Envoi en cours{" "}
                            <div className="ml-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </>
                    ) : (
                        <>
                            Envoyer <i className="ml-2 fas fa-paper-plane"></i>
                        </>
                    )}
                </Button>
            </div>
        </motion.div>,
    ];

    // Add states for accordions
    const [accordions, setAccordions] = useState({
        contact: true,
        schedule: false,
    });

    // Toggle accordion function
    const toggleAccordion = (key) => {
        setAccordions((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    // Add state for active tab
    const [activeTab, setActiveTab] = useState("contact"); // 'contact' or 'schedule'

    // Initialize EmailJS
    useEffect(() => {
        // Initialize EmailJS with your user ID
        emailjs.init("zURIypL0HzxgaUBln");
    }, []);

    return (
        <>
            <SEO
                title="Contact"
                description="Contactez notre agence web pour discuter de votre projet digital ou obtenir un devis personnalisé"
            />
            <GridOverlay />
            <section
                className="pt-32 pb-20 relative overflow-hidden min-h-screen bg-gradient-to-b from-gray-50 via-white to-blue-50"
                ref={sectionRef}
                id="contact"
            >
                {/* Floating elements */}
                <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-gradient-300/20 blur-3xl floating-element"></div>
                <div className="absolute bottom-40 left-20 w-96 h-96 rounded-full bg-purple-300/20 blur-3xl floating-element"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="mb-16 text-center animate-on-scroll opacity-0 translate-y-5 transition-all bg-">
                            {/* <div className="mb-6 mt-4">
                                <motion.span
                                    className="bg-black/80 text-white text-xs md:text-sm px-6 py-2.5 uppercase tracking-wider font-medium shadow-lg rounded-md"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    CONTACTEZ-NOUS
                                </motion.span>
                            </div> */}
                            <div>
                                <motion.h2
                                    className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 title"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    Parlons de votre{" "}
                                    <span className="text-gradient">
                                        projet
                                    </span>
                                </motion.h2>
                            </div>

                            <motion.p
                                className="text-lg text-gray-600 max-w-3xl mx-auto"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                                Nous sommes impatients de discuter de vos
                                besoins et de voir comment nous pouvons vous
                                aider à concrétiser votre vision. Remplissez le
                                formulaire ci-dessous et nous vous répondrons
                                dans les 24 heures.
                            </motion.p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                            {/* Tabs and Content Area */}
                            <div className="lg:col-span-2 relative z-20">
                                {/* Tab Navigation */}
                                <div className="bg-black/5 backdrop-blur-md rounded-t-xl overflow-hidden flex">
                                    <button
                                        onClick={() => setActiveTab("contact")}
                                        className={`flex-1 py-4 px-6 transition-colors ${
                                            activeTab === "contact"
                                                ? "bg-white/90 text-gray-900 font-semibold shadow-sm"
                                                : "bg-transparent text-gray-600 hover:bg-white/30"
                                        }`}
                                    >
                                        <span className="flex items-center justify-center gap-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                            </svg>
                                            Demande d'information
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("schedule")}
                                        className={`flex-1 py-4 px-6 transition-colors ${
                                            activeTab === "schedule"
                                                ? "bg-white/90 text-gray-900 font-semibold shadow-sm"
                                                : "bg-transparent text-gray-600 hover:bg-white/30"
                                        }`}
                                    >
                                        <span className="flex items-center justify-center gap-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            Planifier un appel
                                        </span>
                                    </button>
                                </div>

                                {/* Content Area */}
                                <div className="bg-white/90 backdrop-blur-md rounded-b-xl shadow-lg p-8 relative transition-all duration-500 flex flex-col">
                                    {activeTab === "contact" ? (
                                        <form
                                            onSubmit={handleSubmit}
                                            className="transition-all duration-500 flex-1 flex flex-col"
                                        >
                                            {isSubmitted ? (
                                                <motion.div
                                                    className="text-center py-12 flex-1 items-center justify-center"
                                                    initial={{
                                                        opacity: 0,
                                                        scale: 0.9,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        scale: 1,
                                                    }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 200,
                                                        damping: 20,
                                                    }}
                                                >
                                                    <EnvelopeSVG
                                                        isSubmitted={
                                                            isSubmitted
                                                        }
                                                    />

                                                    <div className="text-primary-500 mt-6 mb-4 flex justify-center">
                                                        <svg
                                                            className="w-16 h-16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                    </div>

                                                    <h3 className="text-2xl font-bold mb-2">
                                                        Message envoyé !
                                                    </h3>
                                                    <p className="text-gray-600 mb-8">
                                                        Merci pour votre
                                                        message. Notre équipe
                                                        vous contactera très
                                                        prochainement.
                                                    </p>
                                                    <Button
                                                        variant="primary"
                                                        onClick={resetForm}
                                                        className="px-6 py-3"
                                                    >
                                                        <i className="fas fa-redo mr-2"></i>{" "}
                                                        Envoyer un nouveau
                                                        message
                                                    </Button>
                                                </motion.div>
                                            ) : (
                                                fieldsets[currentStep]
                                            )}

                                            {/* Indicateur d'étapes */}
                                            {!isSubmitted && (
                                                <div className="flex justify-center mt-6">
                                                    {[0, 1, 2].map((step) => (
                                                        <div
                                                            key={step}
                                                            className={`w-3 h-3 rounded-full mx-1 transition-all ${
                                                                step ===
                                                                currentStep
                                                                    ? "scale-125 bg-black"
                                                                    : "bg-gray-300"
                                                            }`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            )}
                                        </form>
                                    ) : (
                                        <div className="h-full flex-1">
                                            <div
                                                style={{
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    /* hauteur visible = hauteur parent – 60px */
                                                    height: "calc(100% - 60px)",
                                                }}
                                            >
                                                <Cal
                                                    namespace="30min"
                                                    calLink="damien-cousi/30min"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        overflow: "scroll",
                                                        cursor: "auto", // Garantit que le curseur par défaut est normal
                                                    }}
                                                    config={{
                                                        layout: "month_view",
                                                        theme: "light",
                                                        hideEventTypeDetails: false,
                                                        styles: {
                                                            // Styles spécifiques pour les éléments interactifs dans Cal
                                                            branding: {
                                                                buttonColor:
                                                                    "#4f46e5",
                                                                linkColor:
                                                                    "#7c3aed",
                                                            },
                                                            // S'assurer que les éléments cliquables montrent le bon curseur
                                                            enabledDateButton: {
                                                                cursor: "pointer",
                                                            },
                                                            disabledDateButton:
                                                                {
                                                                    cursor: "not-allowed",
                                                                },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information in Accordions */}
                            <div className="lg:col-span-1">
                                <div className="space-y-4 h-full flex flex-col animate-on-scroll opacity-0 translate-y-5 transition-all duration-500">
                                    {/* Contact Information Accordion */}
                                    <Accordion title="Contact">
                                        <div className="space-y-5">
                                            {/* Email */}
                                            <div className="flex items-center">
                                                <div className="bg-white p-2 rounded-md mr-4 flex-shrink-0 shadow">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-4 w-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="black"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 className="text-xs font-semibold uppercase tracking-wider mb-1">
                                                        Email
                                                    </h4>
                                                    <a
                                                        href="mailto:contact@nyloria.fr"
                                                        className="hover:text-gradient transition-colors"
                                                    >
                                                        contact@nyloria.fr
                                                    </a>
                                                </div>
                                            </div>

                                            {/* Phone */}
                                            <div className="flex items-center">
                                                <div className="bg-white p-2 rounded-md mr-4 flex-shrink-0 shadow">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-4 w-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="black"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 className="text-xs font-semibold uppercase tracking-wider mb-1">
                                                        Téléphone
                                                    </h4>
                                                    <a
                                                        href="tel:+33612100572"
                                                        className="hover:text-gradient transition-colors"
                                                    >
                                                        +33 6 12 10 05 72
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Social Media Links */}
                                        <div className="mt-8 border-t border-gray-200 pt-6">
                                            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4">
                                                Suivez-nous
                                            </h4>
                                            <div className="flex space-x-3">
                                                {[
                                                    {
                                                        icon: "fab fa-twitter",
                                                        url:
                                                            socialLinks.twitter ||
                                                            "#",
                                                    },
                                                    {
                                                        icon: "fab fa-linkedin-in",
                                                        url:
                                                            socialLinks.linkedin ||
                                                            "#",
                                                    },
                                                    {
                                                        icon: "fab fa-instagram",
                                                        url:
                                                            socialLinks.instagram ||
                                                            "#",
                                                    },
                                                ].map((social, index) => (
                                                    <a
                                                        key={index}
                                                        href={social.url}
                                                        target="_blank"
                                                        className="group bg-white shadow-sm text-black p-2 rounded-full transition-all duration-300 flex items-center justify-center w-10 h-10"
                                                    >
                                                        <i
                                                            className={`${social.icon} group-hover:animate-bounce`}
                                                        ></i>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </Accordion>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Font Awesome */}
                <link
                    rel="stylesheet"
                    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css"
                />
            </section>
        </>
    );
};

export default Contact;
